import graphqlResource, { GraphqlResource } from 'modules/core/graphql-resource';
import { AddToTeamInputResponseDto } from '../dtos/add-to-team-input-response.dto';

import { AddToTeamInputDto } from '../dtos/add-to-team-input.dto';
import { DeleteTournamentApplicationUserResponseDto } from '../dtos/delete-tournament-application-user-response.dto';
import { GetUserResponseDto } from '../dtos/get-user-response.dto';
import { KickFromTeamInputDto } from '../dtos/kick-from-team-input.dto';
import { UpsertTournamentApplicationUserResponseDto } from '../dtos/upsert-tournament-application-user-response.dto';
import { UpsertTournamentApplicationUserDto } from '../dtos/upsert-tournament-application-user.dto';
import { TournamentUser } from '../interfaces/tournament-user';

export class PlayersRepository {
  constructor(private readonly graphql: GraphqlResource) {}

  public async getUser(params: number): Promise<TournamentUser> {
    const query = `
      query user($id: Int!) {
        user(id: $id) {
          id
          name
          surname
          middleName
          birthDate
          height
          weight
          tournamentAvatars {
            id
            image {
              id
              externalUrl
            }
          }
        }
      }
    `;
    const response = await this.graphql.query<GetUserResponseDto>(query, {
      id: params,
    });
    const { user } = response.data.data;

    return user;
  }

  public async upsertTournamentApplicationForUser(
    params: UpsertTournamentApplicationUserDto,
    tournamentId: number,
  ): Promise<TournamentUser> {
    const query = `#graphql
      mutation upsertTournamentUser($input: CreateUserInput!, $tournamentId: Int!) {
        upsertTournamentUser(input: $input, tournamentId: $tournamentId) {
          id
          name
          surname
          middleName
          birthDate
          height
          weight
          createdAt
          tournamentAvatars {
            id
            image {
              id
              externalUrl
            }
          }
          
        }
      }
    `;

    const variables = { input: { ...params }, tournamentId };
    const response = await this.graphql.query<UpsertTournamentApplicationUserResponseDto>(query, variables);

    if (response.data.errors) {
      throw new Error(response.data.errors[0].message);
    }

    const { upsertTournamentUser } = response.data.data;
    const { id, name, middleName, surname, birthDate, height, weight, tournamentAvatars } = upsertTournamentUser;

    return {
      id,
      name,
      middleName,
      surname,
      birthDate,
      height,
      weight,
      tournamentAvatars,
      createdAt: new Date(upsertTournamentUser.createdAt),
    };
  }

  public async addUserToTeam(params: AddToTeamInputDto): Promise<TournamentUser> {
    const query = `#graphql
      mutation addToTeam($input: AddToTeamInput!) {
        addToTeam(input: $input) {
          id
          name
          surname
          middleName
          birthDate
          photo {
            id
            externalUrl
          }
          height
          weight
          createdAt
          tournamentAvatars {
            id
            image {
              id
              externalUrl
            }
          }
        }
      }
    `;

    const variables = {
      input: {
        ...params,
        // position: params.position
        //   ? params.position.toUpperCase()
        //   : params.position,
      },
    };

    const response = await this.graphql.query<AddToTeamInputResponseDto>(query, variables);

    if (response.data.errors) {
      throw new Error(response.data.errors[0].message);
    }

    const { addToTeam } = response.data.data;
    const { id, name, middleName, surname, birthDate, height, weight, tournamentAvatars } = addToTeam;

    return {
      id,
      name,
      middleName,
      surname,
      birthDate,
      height,
      weight,
      // position: params.position,
      tournamentAvatars,
      createdAt: new Date(addToTeam.createdAt),
    };
  }

  public async deleteTournamentApplicationUser(params: number): Promise<string> {
    const query = `
      mutation deleteTournamentUser($userId: Int!) {
        deleteTournamentUser(userId: $userId) {
          status
        }
      }
    `;

    const response = await this.graphql.query<DeleteTournamentApplicationUserResponseDto>(query, { userId: params });
    const { status } = response.data;

    return status;
  }

  public async kickUserFromTeam(params: KickFromTeamInputDto): Promise<Boolean> {
    const query = `
    mutation kickFromTeam($teamId: Int!, $userId: Int!) {
      kickFromTeam(teamId: $teamId, userId: $userId)
    }
    `;

    const response = await this.graphql.query<Boolean>(query, params);
    return response.data;
  }

  public async suggestTournamentPlayers(tournamentId: number, nameFilter: string): Promise<TournamentUser[]> {
    const query = `
      query suggestTournamentPlayers($id: Int!, $nameFilter: String) {
        tournament(id: $id) {
          users(limit: 20, offset: 0, nameFilter: $nameFilter) {
              id
              isReal
              name
              surname
              middleName
              birthDate
              tournamentAvatars {
                id
                tournament {
                    id
                }
                image {
                    externalUrl
                }
              }
              photo {
                externalUrl
              }
          }
        }
      }
    `;

    const response = await this.graphql.query<{
      data: {
        tournament: {
          users: TournamentUser[];
        };
      };
    }>(query, {
      id: tournamentId,
      nameFilter,
    });

    return response.data.data.tournament.users.filter((user) => !user.isReal);
  }

}

const playersRepository = new PlayersRepository(graphqlResource);

export default playersRepository;
